import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '../types';

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  setAuth: (user: User, token: string) => void;
  setUser: (user: User) => void;
  logout: () => void;
}

/** Strip live api_key before persistence — keys are only shown ephemerally after reset. */
function withoutApiKey(user: User | null): User | null {
  if (!user) return null;
  const { api_key: _omit, ...safe } = user;
  return safe;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      setAuth: (user, token) => {
        localStorage.setItem('awarenow_token', token);
        set({ user: withoutApiKey(user), token, isAuthenticated: true });
      },
      setUser: (user) => set({ user: withoutApiKey(user) }),
      logout: () => {
        localStorage.removeItem('awarenow_token');
        set({ user: null, token: null, isAuthenticated: false });
      },
    }),
    {
      name: 'awarenow_auth',
      // Bump when persisted shape changes; migrate strips legacy api_key.
      version: 1,
      migrate: (persistedState, _fromVersion) => {
        const state = (persistedState ?? {}) as Partial<AuthState>;
        return {
          user: withoutApiKey(state.user ?? null),
          token: state.token ?? null,
          isAuthenticated: Boolean(state.isAuthenticated && state.token),
        };
      },
      // Always sanitize on hydrate — legacy awarenow_auth may still embed api_key
      // even when version already matches (merge bypasses setAuth/setUser).
      merge: (persistedState, currentState) => {
        const persisted = (persistedState ?? {}) as Partial<AuthState>;
        return {
          ...currentState,
          ...persisted,
          user: withoutApiKey(persisted.user ?? null),
        };
      },
      partialize: (state) => ({
        user: withoutApiKey(state.user),
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
